import React, { useState } from 'react';
import {
  Alert, Button, Form, InputNumber, message, Modal, Select,
  Table, Tooltip,
} from 'antd';
import { useQuery } from '@tanstack/react-query';
import {
  destroyPoints, getKPIs, listDomains, transferPoints, stealPoints,
} from '../../services/Admin';
import KPICard from '../../components/KPICard';
import { getDomainStatusEmoji } from '../../utils/utils';

function Stats() {
  // Data retrieving
  const { isLoading, data, refetch } = useQuery(
    ['getKPIs'],
    () => getKPIs(),
    { cacheTime: 0 },
  );
  const { data: domains } = useQuery(
    ['listDomains'],
    () => listDomains({}),
    { cacheTime: 0 },
  );
  const [showDestroyModal, setShowDestroyModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showStealModal, setShowStealModal] = useState(false);
  const [isPerformingAction, setIsPerformingAction] = useState(false);
  const onDestroy = async ({ points }) => {
    try {
      setIsPerformingAction(true);
      await destroyPoints(points);
      message.success('Success');
      setShowDestroyModal(false);
      refetch();
    } catch (error) {
      message.error('Unexpected error while performing action!');
    }
    setIsPerformingAction(false);
  };
  const onTransfer = async ({ domain, points }) => {
    try {
      setIsPerformingAction(true);
      await transferPoints(domain, points);
      message.success('Success');
      setShowTransferModal(false);
      refetch();
    } catch (error) {
      message.error('Unexpected error while performing action!');
    }
    setIsPerformingAction(false);
  };
  const onSteal = async ({ domain, points }) => {
    try {
      setIsPerformingAction(true);
      await stealPoints(domain, points);
      message.success('Success');
      setShowStealModal(false);
      refetch();
    } catch (error) {
      message.error('Unexpected error while performing action!');
    }
    setIsPerformingAction(false);
  };
  return (
    <div id="Users">
      {isLoading && 'Loading...'}
      {data && (
        <>
          {data.domains.points > data.domains.dr && (
            <div style={{ width: 'fit-content', marginLeft: 16 }}>
              <Alert
                type="warning"
                message={`⚠️ ∑ Points > ∑ DR (${data.domains.points - data.domains.dr})`}
              />
            </div>
          )}
          <div className="CardList">
            <KPICard
              number={data.reserve.points}
              title="Reserve"
              subTitle="Points"
            />
            <KPICard
              number={data.domains.count}
              title="Live Domains"
            />
            <KPICard
              number={data.domains.points}
              title="∑ Points"
            />
            <KPICard
              number={data.domains.dr}
              title="∑ DR"
            />
            <KPICard
              number={data.responseRatePercentage}
              title="Response Rate (%)"
            />
          </div>
          <div style={{ marginLeft: 16 }}>
            <Button onClick={() => setShowDestroyModal(true)} danger>
              💥 Destroy Points
            </Button>
            {' '}
            <Button onClick={() => setShowTransferModal(true)} type="primary">
              ✈️ Transfer Points
            </Button>
            {' '}
            <Button onClick={() => setShowStealModal(true)} type="secondary">
              🥷 Steal Points
            </Button>
          </div>
          {['current', 'prev'].map((period) => (
            <React.Fragment key={period}>
              <br />
              <h2 style={{ marginLeft: 16, marginBottom: 0 }}>
                {period === 'current' ? 'Current' : 'Previous'}
                {' '}
                Period Stats
                {` (${data[`${period}PeriodStats`].from.split('T')[0]} → ${data[`${period}PeriodStats`].to.split('T')[0]})`}
              </h2>
              <div className="CardList">
                <KPICard
                  number={data[`${period}PeriodStats`].signups}
                  title="Signups"
                  subTitle="Domain Approved"
                />
                <KPICard
                  number={data[`${period}PeriodStats`].requests}
                  title="Requests"
                  subTitle="Created"
                />
                <KPICard
                  number={data[`${period}PeriodStats`].backlinks}
                  title="Backlinks"
                  subTitle="Shipped"
                />
                <KPICard
                  number={data[`${period}PeriodStats`].responseRatePercentage}
                  title="Response Rate (%)"
                  subTitle="2w Ago"
                />
              </div>
            </React.Fragment>
          ))}
          <br />
          <h2 style={{ marginLeft: 16, marginBottom: 0 }}>Retention</h2>
          <div className="RetentionTable">
            <Table
              dataSource={data.retentionData}
              loading={isLoading}
              pagination={false}
              rowKey="from"
              size="small"
            >
              <Table.Column
                title="Start date"
                className="RetentionTableCellLeft"
                width={100}
                render={(__, d) => d.from.split('T')[0]}
              />
              <Table.Column
                title="Start value"
                className="RetentionTableCellLeft"
                width={100}
                render={(__, d) => d.dataPoints[0].n}
              />
              {new Array(Math.max(data.retentionData[0].dataPoints.length, 12))
                .fill(null)
                .map((_, i) => (
                  <Table.Column
                    className="RetentionTableCell"
                    title={`Week ${i + 1}`}
                    width={70}
                    bordered
                    // eslint-disable-next-line react/no-array-index-key
                    key={i}
                    render={(__, d) => (
                      <div
                        style={{
                          background: `rgba(98, 90, 250, ${(d.dataPoints[i]?.n || 0) / d.dataPoints[0].n})`,
                          height: 40,
                          lineHeight: '40px',
                          color: (d.dataPoints[i]?.n || 0) / d.dataPoints[0].n > 0.5 ? 'white' : 'black',
                        }}
                      >
                        <Tooltip
                          title={d.dataPoints[i] ? (
                            <div>
                              {`Week: ${d.dataPoints[i].from.split('T')[0]}`}
                              <br />
                              {`Value: ${d.dataPoints[i].n}`}
                            </div>
                          ) : null}
                        >
                          {`${((100 * (d.dataPoints[i]?.n || 0)) / d.dataPoints[0].n).toFixed()}%`}
                        </Tooltip>
                      </div>
                    )}
                  />
                ))}
            </Table>
          </div>
        </>
      )}
      {showDestroyModal && (
        <Modal
          visible
          title="Destroy Points"
          footer={null}
          closable={false}
        >
          <Form
            onFinish={onDestroy}
            disabled={isPerformingAction}
            layout="vertical"
            initialValues={{ points: 1 }}
          >
            <Form.Item
              name="points"
              label="Points to destroy"
              rules={[
                { required: true },
              ]}
            >
              <InputNumber min={1} />
            </Form.Item>
            <Form.Item noStyle>
              <div
                style={{
                  textAlign: 'right',
                  marginTop: 16,
                }}
              >
                <Button onClick={() => setShowDestroyModal(false)}>
                  CANCEL
                </Button>
                {' '}
                <Button htmlType="Submit" loading={isPerformingAction} type="primary" danger>
                  DESTROY
                </Button>
              </div>
            </Form.Item>
          </Form>
        </Modal>
      )}
      {showTransferModal && (
        <Modal
          visible
          title="Transfer Points"
          footer={null}
          closable={false}
        >
          <Form
            onFinish={onTransfer}
            disabled={isPerformingAction}
            layout="vertical"
            initialValues={{ points: 1 }}
          >
            <Form.Item
              name="domain"
              rules={[{ required: true }]}
            >
              <Select
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) => {
                  const { children } = option.props;
                  const searchText = children.toLowerCase();
                  return searchText.includes(input.toLowerCase());
                }}
              >
                {(domains || []).map((domain) => (
                  <Select.Option value={domain._id} key={domain._id}>
                    {`${getDomainStatusEmoji(domain)} ${domain.name} (${domain.userIds[0].email})`}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="points"
              label="Points to transfer"
              rules={[
                { required: true },
              ]}
            >
              <InputNumber min={1} />
            </Form.Item>
            <Form.Item noStyle>
              <div
                style={{
                  textAlign: 'right',
                  marginTop: 16,
                }}
              >
                <Button onClick={() => setShowTransferModal(false)}>
                  CANCEL
                </Button>
                {' '}
                <Button htmlType="Submit" loading={isPerformingAction} type="primary">
                  TRANSFER
                </Button>
              </div>
            </Form.Item>
          </Form>
        </Modal>
      )}
      {showStealModal && (
        <Modal
          visible
          title="Steal Points"
          footer={null}
          closable={false}
        >
          <Form
            onFinish={onSteal}
            disabled={isPerformingAction}
            layout="vertical"
            initialValues={{ points: 1 }}
          >
            <Form.Item
              name="domain"
              rules={[{ required: true }]}
            >
              <Select
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) => {
                  const { children } = option.props;
                  const searchText = children.toLowerCase();
                  return searchText.includes(input.toLowerCase());
                }}
              >
                {(domains || []).map((domain) => (
                  <Select.Option value={domain._id} key={domain._id}>
                    {`${getDomainStatusEmoji(domain)} ${domain.name} (${domain.userIds[0].email})`}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="points"
              label="Points to steal"
              rules={[
                { required: true },
              ]}
            >
              <InputNumber min={1} />
            </Form.Item>
            <Form.Item noStyle>
              <div
                style={{
                  textAlign: 'right',
                  marginTop: 16,
                }}
              >
                <Button onClick={() => setShowStealModal(false)}>
                  CANCEL
                </Button>
                {' '}
                <Button htmlType="Submit" loading={isPerformingAction} type="primary">
                  STEAL
                </Button>
              </div>
            </Form.Item>
          </Form>
        </Modal>
      )}
    </div>
  );
}

Stats.propTypes = {};

export default Stats;
